import { ViewEncapsulation, Component, OnDestroy, HostBinding } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription, of, forkJoin, switchMap } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { NgIf, NgFor, DatePipe } from '@angular/common';

import { Offer, OfferProduct, OfferFamilyVolume } from '../../../models/offer';
import { offerHeaderIdCreate } from '../../../models/offer-utils';
import { OfferService } from '../../../services/offer.api.service';
import { AppOfferService } from '../../../services/app-offer.service';
import { pageTransitions } from '../../../../shared/animations/page-transitions';
import { UserService } from '../../../../shared/services/user.service';
import { AppWizardService } from '../../../services/app-wizard.service';
import { BocClient } from '../../../../shared/models/boc-client-transac';
import { AppWizardStateOffer } from '../../../models/app-wizard-state';
import { UNDERSCORE } from '../../../../shared/utils/constant';
import { LocationService } from '../../../../shared/services/location.service';
import { OfferProductCardComponent } from '../offer-product-card/offer-product-card.component';
import { OfferHeaderCardBlockComponent } from '../offer-header-card-block/offer-header-card-block.component';
import { AfterViewInitDirective } from '../../../../shared/directives/after-view-init.directive';
import { FocusMeDirective } from '../../../../shared/directives/focus-me.directive';
import { LoadingSpinnerComponent } from '../../../../shared/components/utils/loading-spinner.component';
import { ScrollerDirective } from '../../../../shared/directives/scroller.directive';

interface ProductsFamilyMod {
	productFamilyCategory: string;
	form: string;
	familyCode: string;
	quantity?: number;
	products: OfferProduct[];
	volumeList: OfferFamilyVolume[];
}
@Component({
	selector: 'copo-offer-offer-card',
	templateUrl: './offer-card.component.html',
	animations: pageTransitions,
	styleUrl: './offer-card.component.css',
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		ScrollerDirective,
		NgIf,
		LoadingSpinnerComponent,

		MatButtonModule,
		MatIconModule,
		TranslateModule,
		FocusMeDirective,
		AfterViewInitDirective,
		OfferHeaderCardBlockComponent,
		NgFor,
		OfferProductCardComponent,
		DatePipe
	]
})
export class OfferCardComponent implements OnDestroy {
	@HostBinding('class.passthrough') readonly passthrough = true;

	productsFamilies?: ProductsFamilyMod[];
	pdtsFamilies?: ProductsFamilyMod[];
	isLoading = true;
	isError = false;
	offer?: Offer;
	offerBocs?: BocClient[];
	wizardStep: number | undefined;
	readonly hasUserSubmitterRole: boolean;
	isDraft = false;
	isAccepted = false;
	private readonly offerSubscription: Subscription;

	constructor(
		private readonly route: ActivatedRoute,
		private readonly userService: UserService,
		private readonly offerService: OfferService,
		private readonly appOfferService: AppOfferService,
		private readonly appWizardService: AppWizardService,
		private readonly locationService: LocationService
	) {
		this.hasUserSubmitterRole = this.userService.user.isOfferSubmitter();
		const offerId = <string> this.route.snapshot.params['offerId'];
		this.offerSubscription = this.offerService.getOffer(
			offerId,
			true,
			'INLINE'
		)
			.pipe(switchMap(offer => forkJoin([of(offer), this.offerService.getOfferBocs(offer.header)])))
			.subscribe({
				next: ([offer, bocPage]) => {
					this.offer = offer;
					this.isLoading = false;
					this.offerBocs = bocPage.results;
					this.isDraft = offer.header.externalId === undefined && (
						offer.products === undefined
						|| offer.products.filter(p => p.offerStatus !== 'DRAFT').length === 0
					);
					if (this.isDraft && !(this.userService.user.hasService('EOFFR')
						&& this.userService.user.isOfferValidator())) {
						this.isLoading = false;
						this.isError = true;
						this.appOfferService.showError('Access to Draft card is Forbidden');
						throw new Error('Access to Draft card is Forbidden');
					}
					this.isAccepted = offer.header.externalId === undefined
						&& offer.products !== undefined && offer.products.length > 0
						&& offer.products.filter(p =>
							p.offerStatus !== 'ACCEPTED' && p.offerStatus !== 'CANCELED').length === 0;
					this.wizardStep = this.isDraft ? AppWizardStateOffer.COMPUTE_INDEX_STEP(offer) + 1 : undefined;
					this.productsFamilies = this.createProductsFamilies(this.offer);
				},
				error: (err: unknown) => {
					this.isLoading = false;
					this.isError = true;
					console.error('unable to load offer', err);
					this.appOfferService.showError();
				}
			});
	}

	ngOnDestroy() {
		this.offerSubscription.unsubscribe();
	}

	async evtContinueDraft() {
		if (!this.offer) {
			return;
		}
		this.isLoading = true;
		await this.appWizardService.actionEditOffer(offerHeaderIdCreate(this.offer.header));
		this.isLoading = false;
	}

	async evtCopyOffer() {
		if (!this.offer) {
			return;
		}
		this.isLoading = true;
		await this.appWizardService.actionCopyOffer(offerHeaderIdCreate(this.offer.header));
		this.isLoading = false;
	}

	goBack() {
		this.locationService.back();
	}

	createProductsFamilies(offer: Offer) {
		if (!offer.products || offer.products.length === 0) {
			return [];
		}
		this.pdtsFamilies = [];
		for (const product of offer.products) {
			const { family } = product.description;

			// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
			if (family) {
				const famlyCode = family.code || UNDERSCORE;
				let productsFamily = this.pdtsFamilies.find(x => x.familyCode === famlyCode);
				if (productsFamily) {
					if (product.quantity && productsFamily.quantity) {
						productsFamily.quantity += product.quantity;
					}
					productsFamily.products.push(product);
				} else {
					productsFamily = {
						productFamilyCategory: family.subCategory || UNDERSCORE,
						form: family.form || UNDERSCORE,
						familyCode: famlyCode,
						quantity: product.quantity || 0,
						products: [product],
						volumeList: []
					};
					this.pdtsFamilies.push(productsFamily);
				}
			}
		}

		// Handle volume split by family
		if (offer.listOfOfferFamilyVolume && offer.header.offerType === 'framework') {
			for (const volSplit of offer.listOfOfferFamilyVolume) {
				const productsFamily = this.pdtsFamilies.find(x => x.familyCode === volSplit.familyCode);
				if (productsFamily) {
					if (volSplit.quantity && productsFamily.quantity !== undefined) {
						productsFamily.quantity += volSplit.quantity;
					}
					productsFamily.volumeList.push(volSplit);
				}
			}
		}

		return this.pdtsFamilies;
	}
}
